<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Berita', href: route('berita.index') },
          ]"
        />

        <!-- Header Section -->
        <div class="mb-4 sm:mb-6">
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Daftar Berita</h3>
              <p class="mt-1 text-sm text-gray-600">
                Kelola berita dan artikel sistem
              </p>
            </div>
            <div class="mt-4 sm:mt-0">
              <Link
                v-if="$page.props.auth.user && $page.props.can.berita_create"
                :href="route('berita.create')"
                class="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              >
                <i class="mr-2 fa-solid fa-plus"></i>
                Tambah Berita
              </Link>
            </div>
          </div>
        </div>

        <!-- Search Input -->
        <div class="relative mb-4">
          <div
            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
          >
            <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
          </div>
          <input
            v-model="search"
            type="text"
            autocomplete="off"
            placeholder="Cari berita..."
            id="search"
            class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
          />
        </div>

        <!-- Table -->
        <div
          class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
        >
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[200px]"
                  >
                    Judul
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[250px]"
                  >
                    Konten
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell min-w-[150px]"
                  >
                    Slug
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[100px]"
                  >
                    Gambar
                  </th>
                  <th
                    scope="col"
                    class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[120px]"
                  >
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <!-- Table Rows -->
                <tr
                  v-for="(newsItem, index) in news.data"
                  :key="newsItem.id"
                  class="transition-colors duration-150 hover:bg-gray-50"
                >
                  <td
                    class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                  >
                    {{ getRowNumber(index) }}
                  </td>
                  <td class="px-3 py-4 text-sm">
                    <div class="flex items-start">
                      <span class="mr-2 text-indigo-600 mt-0.5">📰</span>
                      <div>
                        <div class="font-medium text-gray-900 line-clamp-2">
                          {{ newsItem.title }}
                        </div>
                        <!-- Mobile view: Show content under title on small screens -->
                        <div
                          class="mt-1 text-xs text-gray-500 md:hidden line-clamp-2"
                          v-html="newsItem.content"
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td
                    class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                  >
                    <div class="line-clamp-3" v-html="newsItem.content"></div>
                  </td>
                  <td
                    class="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell"
                  >
                    <span
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full"
                    >
                      {{ newsItem.slug }}
                    </span>
                  </td>
                  <td class="px-3 py-4 text-sm whitespace-nowrap">
                    <div class="flex items-center">
                      <img
                        v-if="newsItem.image"
                        :src="newsItem.image"
                        alt="Gambar Berita"
                        class="object-cover w-10 h-10 border rounded-lg sm:w-12 sm:h-12"
                      />
                      <span
                        v-else
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full"
                      >
                        <i class="mr-1 fa-solid fa-image"></i>
                        No Image
                      </span>
                    </div>
                  </td>
                  <td
                    class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                  >
                    <div
                      class="flex items-center justify-end space-x-1 sm:space-x-2"
                    >
                      <!-- Action Buttons -->
                      <Link
                        v-if="$page.props.can.berita_show"
                        :href="route('berita.show', newsItem.idnews)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                        title="Lihat Detail"
                      >
                        <i class="w-3 h-3 fa-solid fa-eye"></i>
                        <span class="hidden ml-1 sm:inline">Lihat</span>
                      </Link>
                      <button
                        v-if="$page.props.can.berita_delete"
                        @click="deleteNews(newsItem.idnews)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                        title="Hapus Berita"
                      >
                        <i class="w-3 h-3 fa-solid fa-trash"></i>
                        <span class="hidden ml-1 sm:inline">Hapus</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination -->
        <Pagination :data="news" :updatedPageNumber="updatedPageNumber" />
      </div>
    </template>
  </Container>
</template>
<script setup>
import { ref, onMounted, computed, watch } from "vue";
import Container from "@/Layouts/Container.vue";
import Pagination from "@/Components/Pagination.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Swal from "sweetalert2";
import { router, useForm, usePage } from "@inertiajs/vue3";

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  allTags: {
    type: Array,
    required: true,
  },
});

/**Pagination */
let search = ref(usePage().props.search),
  pageNumber = ref(1);

let newsUrl = computed(() => {
  let url = new URL(route("berita.index"));
  url.searchParams.append("page", pageNumber.value);
  if (search.value) {
    url.searchParams.append("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  pageNumber.value = link.url.split("=")[1];
};

// Row number calculation with fallback for pagination
const getRowNumber = (index) => {
  const currentPage = props.news.current_page || 1;
  const perPage = props.news.per_page || 10;
  const calculated = (currentPage - 1) * perPage + index + 1;
  return isNaN(calculated) ? index + 1 : calculated;
};

watch(
  () => newsUrl.value,
  (updateNewsUrl) => {
    router.visit(updateNewsUrl, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
  }
);
//End of Pagination

// Delete function with improved SweetAlert2
const deleteNews = async (newsId) => {
  const result = await Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Berita akan dihapus permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  });

  if (result.isConfirmed) {
    // Show loading
    Swal.fire({
      title: "Menghapus...",
      text: "Sedang menghapus berita",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    router.delete(route("berita.destroy", newsId), {
      onSuccess: () => {
        Swal.fire({
          title: "Terhapus!",
          text: "Berita berhasil dihapus",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Terjadi kesalahan saat menghapus berita",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  }
};

function addTag(newTag) {
  const exists =
    form.tags.some((tag) => tag.name === newTag) ||
    props.allTags.some((tag) => tag.name === newTag);

  if (!exists) {
    form.tags.push({ name: newTag });
  }
}

const form = useForm({
  id: null,
  title: "",
  content: "",
  publish_date: "",
  published: "",
  tags: [],
  image: [],
  imagePreview: [],
});

function handleFileChange(e) {
  const files = Array.from(e.target.files);
  form.image = files;
  form.imagePreview = files.map((file) => URL.createObjectURL(file));
}

onMounted(() => {
  const page = usePage();
  if (page.props.flash && page.props.flash.success) {
    Swal.fire({
      title: "Berhasil!",
      text: page.props.flash.success,
      icon: "success",
      confirmButtonText: "OK",
    });
  }
});

const fileInput = ref(null);
const quillRef = ref(null);

const submit = async () => {
  const selectedTags = form.tags.map((tag) => tag.name ?? tag);
  const formData = new FormData();
  formData.append("title", form.title);
  formData.append("content", form.content);
  formData.append("publish_date", form.publish_date);
  formData.append("published", form.published);

  if (Array.isArray(selectedTags)) {
    selectedTags.forEach((tag) => {
      formData.append("tags[]", tag);
    });
  }

  if (Array.isArray(form.image)) {
    form.image.forEach((file, index) => {
      formData.append(`image[${index}]`, file);
    });
  }

  form.errors = {};

  try {
    const response = await axios.post(route("berita.store"), formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    Swal.fire({
      title: "Berhasil!",
      text: "Berita berhasil disimpan",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      window.location.reload();
      form.reset();
      form.image = [];
      form.imagePreview = [];
      if (fileInput.value) fileInput.value.value = "";
      form.tags = [];
      form.title = "";
      form.content = "";
      form.publish_date = "";
      form.published = "";
      if (quillRef.value) {
        quillRef.value.setHTML && quillRef.value.setHTML("");
        if (quillRef.value.editor && quillRef.value.editor.setContents) {
          quillRef.value.editor.setContents([]);
        }
      }
    });
  } catch (error) {
    if (error.response && error.response.status === 422) {
      form.errors = error.response.data.errors;
    } else {
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat menyimpan berita",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }
};
</script>

<style scoped>
/* Ensure proper text truncation */
:deep(.line-clamp-2) {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.line-clamp-3) {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Clean HTML content styling */
:deep(.line-clamp-2 *),
:deep(.line-clamp-3 *) {
  margin: 0 !important;
  padding: 0 !important;
  font-size: inherit !important;
  line-height: inherit !important;
}
</style>
